import { Redirect, router, useGlobalSearchParams } from "expo-router";
import { useMemo, useState } from "react";
import { Pressable, StyleSheet, View, TextInput } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import { getEntityPermissions } from "@/lib/permissions";
import { ThemedText } from "@/components/ThemedText";
import { Drawer } from "expo-router/drawer";
import { Button } from "@/components/ui/Button";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { DisplayNamesHeaderProvider, useDisplayNamesHeader } from "@/contexts/DisplayNamesHeaderContext";
import { SettingsSearchProvider, useSettingsSearch } from "@/contexts/SettingsSearchContext";
import {
  DrawerContentScrollView,
  DrawerItem,
  DrawerContentComponentProps,
  DrawerNavigationOptions,
} from "@react-navigation/drawer";

const DRAWER_SECTIONS = [
  { label: "Account", routes: ["user", "about"] },
  { label: "Administration", routes: ["roles", "tenant", "display_names"] },
  {
    label: "Management",
    routes: [
      "markets",
      "locations",
      "positions",
      "dress_codes",
      "billing_items",
      "incident_types",
      "abilities",
      "restrictions",
      "unions",
    ],
  },
];

const ROUTE_ICONS: Record<string, keyof typeof MaterialCommunityIcons.glyphMap> = {
  user: "account",
  about: "information",
  roles: "shield-account",
  tenant: "domain",
  display_names: "form-textbox",
  markets: "map-marker",
  locations: "map-marker-multiple",
  positions: "briefcase",
  dress_codes: "tshirt-crew",
  billing_items: "currency-usd",
  incident_types: "alert-circle",
  abilities: "certificate",
  restrictions: "cancel",
  unions: "handshake",
};

function CustomDrawerContent(props: DrawerContentComponentProps & { isDesktop: boolean }) {
  const { colors } = useTheme();
  const globalParams = useGlobalSearchParams();
  const referer = globalParams.referer as string | undefined;

  const handleBack = () => {
    if (referer) {
      try {
        const { pathname, params } = JSON.parse(referer) as { pathname: string; params: Record<string, string> };
        router.navigate({ pathname: pathname as any, params });
      } catch {
        router.navigate("/");
      }
    } else {
      router.navigate("/");
    }
  };

  const { state, descriptors, navigation } = props;

  return (
    <DrawerContentScrollView {...props} contentContainerStyle={{ paddingTop: 0 }}>
      <Pressable
        onPress={handleBack}
        style={({ pressed }) => [
          drawerStyles.backButton,
          { backgroundColor: pressed ? colors.backgroundSecondary : "transparent" },
        ]}
      >
        <MaterialCommunityIcons name="arrow-left" size={20} color={colors.primary} />
        <ThemedText style={{ color: colors.primary, fontWeight: "500" }}>Back</ThemedText>
      </Pressable>

      {DRAWER_SECTIONS.map((section, sectionIndex) => {
        const visibleRoutes = section.routes
          .map((routeName) => {
            const routeIndex = state.routes.findIndex((r) => r.name === routeName);
            if (routeIndex === -1) return null;
            const route = state.routes[routeIndex];
            const descriptor = descriptors[route.key];
            if ((descriptor.options as any).href === null) return null;
            return { route, routeIndex, descriptor };
          })
          .filter(Boolean) as {
          route: (typeof state.routes)[number];
          routeIndex: number;
          descriptor: (typeof descriptors)[string];
        }[];

        if (visibleRoutes.length === 0) return null;

        return (
          <View key={section.label}>
            {sectionIndex > 0 && <View style={[drawerStyles.separator, { backgroundColor: colors.border }]} />}
            <ThemedText style={[drawerStyles.sectionHeader, { color: colors.textSecondary }]}>
              {section.label}
            </ThemedText>
            {visibleRoutes.map(({ route, routeIndex, descriptor }) => {
              const isActive = state.index === routeIndex;
              return (
                <View key={route.key} style={{ flexDirection: "row", alignItems: "center" }}>
                  <View
                    style={{
                      width: 3,
                      height: 18,
                      borderRadius: 1.5,
                      marginLeft: 4,
                      backgroundColor: isActive && props.isDesktop ? colors.primary : "transparent",
                    }}
                  />
                  <DrawerItem
                    label={descriptor.options.title ?? route.name}
                    focused={isActive}
                    activeTintColor={props.isDesktop ? colors.text : "white"}
                    activeBackgroundColor={props.isDesktop ? colors.backgroundHover : colors.primary}
                    inactiveTintColor={colors.textSecondary}
                    icon={({ color }) => (
                      <MaterialCommunityIcons name={ROUTE_ICONS[route.name] ?? "circle"} size={20} color={color} />
                    )}
                    labelStyle={{ fontSize: 14 }}
                    style={{
                      flex: 1,
                      borderRadius: 8,
                      marginVertical: 1,
                      marginHorizontal: 4,
                      marginRight: 8,
                      height: 36,
                      justifyContent: "center",
                    }}
                    onPress={() => navigation.navigate(route.name)}
                  />
                </View>
              );
            })}
          </View>
        );
      })}
    </DrawerContentScrollView>
  );
}

const drawerStyles = StyleSheet.create({
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginHorizontal: 8,
    marginTop: 8,
    marginBottom: 4,
    borderRadius: 12,
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 4,
  },
  separator: {
    height: StyleSheet.hairlineWidth,
    marginHorizontal: 16,
    marginTop: 8,
  },
});

type SettingsPage =
  | "positions"
  | "dress_codes"
  | "markets"
  | "billing_items"
  | "incident_types"
  | "abilities"
  | "restrictions";

interface SettingsHeaderProps {
  page: SettingsPage;
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  title: string;
  subtitle: string;
  canCreate: boolean;
  createText: string;
}

function SettingsHeader({ page, icon, title, subtitle, canCreate, createText }: SettingsHeaderProps) {
  const { colors } = useTheme();
  const { searchValue, setSearchValue, searchFocused, setSearchFocused } = useSettingsSearch(page);

  return (
    <View style={headerStyles.container}>
      <View style={headerStyles.topRow}>
        <View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <MaterialCommunityIcons name={icon} size={24} color={colors.text} />
            <ThemedText type="title">{title}</ThemedText>
          </View>
          <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>{subtitle}</ThemedText>
        </View>

        {canCreate && (
          <Button
            style={{
              paddingHorizontal: 8,
              height: 35,
              backgroundColor: colors.green.background,
            }}
            textStyle={{ color: "white" }}
            text={createText}
            onPress={() => router.setParams({ create: "true" })}
          />
        )}
      </View>

      <View style={headerStyles.searchRow}>
        <View
          style={[headerStyles.searchContainer, { borderBottomColor: searchFocused ? colors.primary : colors.border }]}
        >
          <MaterialCommunityIcons
            name="magnify"
            size={20}
            color={searchFocused ? colors.primary : colors.textSecondary}
          />
          <TextInput
            value={searchValue}
            onChangeText={setSearchValue}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search..."
            placeholderTextColor={colors.textSecondary}
            style={[headerStyles.searchInput, { color: colors.text, outlineStyle: "none" } as any]}
          />
          {searchValue.length > 0 && (
            <Pressable onPress={() => setSearchValue("")}>
              <MaterialCommunityIcons name="close-circle" size={18} color={colors.textSecondary} />
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );
}

const headerStyles = StyleSheet.create({
  container: {
    gap: 12,
  },
  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  searchRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 6,
    borderBottomWidth: 1,
    width: 200,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    padding: 0,
  },
});

function DisplayNamesHeader() {
  const { colors } = useTheme();
  const { dirty, saving, onSave, onReset } = useDisplayNamesHeader();

  return (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        // paddingHorizontal: 16,
      }}
    >
      <View style={{ flex: 1 }}>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <MaterialCommunityIcons name="form-textbox" size={24} color={colors.text} />
          <ThemedText type="title">Display Names</ThemedText>
        </View>
        <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>
          Customize how entities appear throughout your app
        </ThemedText>
      </View>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <ThemedText style={{ opacity: dirty ? 1 : 0.5, fontSize: 12 }}>
          {dirty ? "Unsaved changes" : "Saved"}
        </ThemedText>
        <Button
          text="Reset"
          icon="backup-restore"
          style={{
            paddingHorizontal: 8,
            height: 35,
            backgroundColor: "transparent",
            borderWidth: 1,
            borderColor: colors.border,
          }}
          textStyle={{ fontSize: 13 }}
          enabled={dirty && !!onReset}
          onPress={() => onReset?.()}
        />
        <Button
          text={saving ? "Saving..." : "Save"}
          icon="content-save"
          iconColor="white"
          style={{
            paddingHorizontal: 8,
            height: 35,
            backgroundColor: colors.primary,
          }}
          textStyle={{ fontSize: 13, color: "white" }}
          enabled={dirty && !saving && !!onSave}
          onPress={() => onSave?.()}
        />
      </View>
    </View>
  );
}

function SettingsLayoutContent({ routes = { user: "user" } }: { routes: Record<string, string> }) {
  const { session, loading, userRoles } = useAuth();
  const { colors } = useTheme();
  const [width, setWidth] = useState(1800);
  const isDesktopLayout = width >= 900;

  // Permission checks for read access (controls href visibility)
  const hasRolesAccess = useMemo(() => getEntityPermissions(userRoles, "roles").read, [userRoles]);
  const hasLocationsAccess = useMemo(() => getEntityPermissions(userRoles, "locations").read, [userRoles]);
  const hasUnionsAccess = useMemo(() => getEntityPermissions(userRoles, "unions").read, [userRoles]);
  const hasMarketsAccess = useMemo(() => getEntityPermissions(userRoles, "markets").read, [userRoles]);
  const hasPositionsAccess = useMemo(() => getEntityPermissions(userRoles, "positions").read, [userRoles]);
  const hasAbilitiesAccess = useMemo(() => getEntityPermissions(userRoles, "abilities").read, [userRoles]);
  const hasRestrictionsAccess = useMemo(() => getEntityPermissions(userRoles, "restrictions").read, [userRoles]);
  const hasDressCodesAccess = useMemo(() => getEntityPermissions(userRoles, "dress_codes").read, [userRoles]);
  const hasBillingItemsAccess = useMemo(() => getEntityPermissions(userRoles, "billing_items").read, [userRoles]);
  const hasIncidentTypesAccess = useMemo(() => getEntityPermissions(userRoles, "incident_types").read, [userRoles]);
  const hasTenantAccess = useMemo(() => getEntityPermissions(userRoles, "tenant").update, [userRoles]);
  // Permission checks for create access (controls header buttons)
  const canCreateMarkets = useMemo(() => getEntityPermissions(userRoles, "markets").create, [userRoles]);
  const canCreatePositions = useMemo(() => getEntityPermissions(userRoles, "positions").create, [userRoles]);
  const canCreateDressCodes = useMemo(() => getEntityPermissions(userRoles, "dress_codes").create, [userRoles]);
  const canCreateBillingItems = useMemo(() => getEntityPermissions(userRoles, "billing_items").create, [userRoles]);
  const canCreateIncidentTypes = useMemo(() => getEntityPermissions(userRoles, "incident_types").create, [userRoles]);
  const canCreateAbilities = useMemo(() => getEntityPermissions(userRoles, "abilities").create, [userRoles]);
  const canCreateRestrictions = useMemo(() => getEntityPermissions(userRoles, "restrictions").create, [userRoles]);

  // Memoize screen options to prevent recreating on every render
  const screenOptions: DrawerNavigationOptions = useMemo(
    () => ({
      drawerType: (isDesktopLayout ? "permanent" : "slide") as "permanent" | "slide",
      sceneStyle: {
        backgroundColor: colors.backgroundSecondary,
        borderColor: colors.border,
        paddingVertical: 16,
        paddingHorizontal: isDesktopLayout ? Math.max(16, Math.round((width - 1400) / 2)) : 16,
      },
      drawerStyle: { backgroundColor: colors.backgroundSecondary },
      drawerLabelStyle: { color: colors.text },
      drawerActiveBackgroundColor: colors.primary,
      drawerActiveTintColor: "white",
      drawerItemStyle: { borderRadius: 12 },
      headerStyle: { backgroundColor: colors.backgroundSecondary },
      headerShown: true,
      headerBackButtonDisplayMode: "minimal" as const,
      headerLeft: () => null,
      headerShadowVisible: false,
    }),
    [colors, isDesktopLayout, width],
  );

  return !session && !loading ? (
    <Redirect href="/sign-in" />
  ) : (
    <View style={{ flex: 1 }} onLayout={(e) => setWidth(e.nativeEvent.layout.width)}>
      <Drawer
        initialRouteName={routes["user"]}
        backBehavior="order"
        screenOptions={screenOptions}
        drawerContent={(props) => <CustomDrawerContent {...props} isDesktop={isDesktopLayout} />}
      >
        <Drawer.Screen
          name="user"
          options={{
            title: "User",
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="roles"
          options={{
            title: "Roles",
            headerShown: false,
            ...(hasRolesAccess ? {} : { href: null }),
          }}
        />
        <Drawer.Screen
          name="tenant"
          options={{
            title: "Tenant",
            headerShown: false,
            ...(hasTenantAccess ? {} : { href: null }),
          }}
        />
        <Drawer.Screen
          name="display_names"
          options={{
            title: "Display Names",
            ...(hasTenantAccess ? {} : { href: null }),
            header: () => <DisplayNamesHeader />,
          }}
        />
        <Drawer.Screen
          name="markets"
          options={{
            title: "Markets",
            ...(hasMarketsAccess ? {} : { href: null }),
            header: () => (
              <SettingsHeader
                page="markets"
                icon="map-marker"
                title="Markets"
                subtitle="Geographic markets and their FIPS codes"
                canCreate={canCreateMarkets}
                createText="New Market"
              />
            ),
          }}
        />
        <Drawer.Screen
          name="locations"
          options={{
            title: "Locations",
            ...(hasLocationsAccess ? {} : { href: null }),
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="positions"
          options={{
            title: "Positions",
            ...(hasPositionsAccess ? {} : { href: null }),
            header: () => (
              <SettingsHeader
                page="positions"
                icon="briefcase"
                title="Positions"
                subtitle="Manage billing and payroll rates"
                canCreate={canCreatePositions}
                createText="New Position"
              />
            ),
          }}
        />
        <Drawer.Screen
          name="dress_codes"
          options={{
            title: "Dress Codes",
            ...(hasDressCodesAccess ? {} : { href: null }),
            header: () => (
              <SettingsHeader
                page="dress_codes"
                icon="tshirt-crew"
                title="Dress Codes"
                subtitle="Manage dress code requirements for shifts"
                canCreate={canCreateDressCodes}
                createText="New Dress Code"
              />
            ),
          }}
        />
        <Drawer.Screen
          name="billing_items"
          options={{
            title: "Billing Items",
            ...(hasBillingItemsAccess ? {} : { href: null }),
            header: () => (
              <SettingsHeader
                page="billing_items"
                icon="currency-usd"
                title="Billing Items"
                subtitle="Billable services and cost items"
                canCreate={canCreateBillingItems}
                createText="New Billing Item"
              />
            ),
          }}
        />
        <Drawer.Screen
          name="incident_types"
          options={{
            title: "Incident Types",
            ...(hasIncidentTypesAccess ? {} : { href: null }),
            header: () => (
              <SettingsHeader
                page="incident_types"
                icon="alert-circle"
                title="Incident Types"
                subtitle="Categories for incident reporting"
                canCreate={canCreateIncidentTypes}
                createText="New Incident Type"
              />
            ),
          }}
        />
        <Drawer.Screen
          name="abilities"
          options={{
            title: "Abilities",
            ...(hasAbilitiesAccess ? {} : { href: null }),
            header: () => (
              <SettingsHeader
                page="abilities"
                icon="certificate"
                title="Abilities"
                subtitle="Qualifications and certifications that users can possess"
                canCreate={canCreateAbilities}
                createText="New Ability"
              />
            ),
          }}
        />
        <Drawer.Screen
          name="restrictions"
          options={{
            title: "Restrictions",
            ...(hasRestrictionsAccess ? {} : { href: null }),
            header: () => (
              <SettingsHeader
                page="restrictions"
                icon="cancel"
                title="Restrictions"
                subtitle="Limitations or constraints that may apply to users"
                canCreate={canCreateRestrictions}
                createText="New Restriction"
              />
            ),
          }}
        />
        <Drawer.Screen
          name="about"
          options={{
            title: "About",
            headerShown: false,
          }}
        />
        <Drawer.Screen
          name="unions"
          options={{
            title: "Unions",
            headerShown: false,
            ...(hasUnionsAccess ? {} : { href: null }),
          }}
        />
      </Drawer>
    </View>
  );
}

export default function SettingsLayout(props: { routes: Record<string, string> }) {
  return (
    <DisplayNamesHeaderProvider>
      <SettingsSearchProvider>
        <SettingsLayoutContent {...props} />
      </SettingsSearchProvider>
    </DisplayNamesHeaderProvider>
  );
}
